import { useState, useEffect, useRef } from 'react';
import { BrowserRouter as Router, Routes, Route, useNavigate, Link } from 'react-router-dom';
import './App.css';
import { useSearchParams } from 'react-router-dom';
const earlyAccessFormLink = 'https://tally.so/r/m6BZek';

function Invite() {
  const [searchParams] = useSearchParams();
  const [inviteKey, setInviteKey] = useState('');

  useEffect(() => {
    const key = searchParams.get('invite-key');
    if (key) {
      setInviteKey(key);
    }
  }, [searchParams]);

  useEffect(() => {
    document.title = 'Join TimeKap - Invite';
  }, []);

  const handleDownload = (platform) => {
    // TODO: Replace with actual app store links when available
    if (platform === 'ios') {
      window.open('https://apps.apple.com/app/timekap', '_blank');
    } else if (platform === 'android') {
      window.open('https://play.google.com/store/apps/details?id=com.timekap', '_blank');
    }
  };

  return (
    <div className="min-h-screen bg-[#fdf8f3] flex items-center justify-center px-4">
      <div className="max-w-lg w-full bg-white rounded-2xl shadow-xl p-8">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">You've Been Invited!</h1>
          <p className="text-gray-600">Someone special wants to share memories with you on TimeKap</p>
        </div>

        {inviteKey ? (
          <div className="mb-8">
            <div className="bg-gradient-to-r from-[#ffce3e] to-[#ffd966] rounded-xl p-6 mb-6 text-center shadow-lg border border-yellow-200">
              <p className="text-sm font-medium text-gray-700 mb-3">Your TimeKap Invite Code</p>
              <div 
                className="bg-white rounded-lg p-4 shadow-sm cursor-pointer hover:shadow-md transition-all duration-200 hover:bg-gray-50 group"
                onClick={async () => {
                  try {
                    await navigator.clipboard.writeText(inviteKey);
                    // Show success feedback
                    const button = document.getElementById('copy-feedback');
                    if (button) {
                      button.textContent = 'Copied!';
                      button.classList.add('text-green-600');
                      setTimeout(() => {
                        button.textContent = 'Click anywhere on the code area to copy';
                        button.classList.remove('text-green-600');
                      }, 2000);
                    }
                  } catch (err) {
                    // Fallback for older browsers
                    const textArea = document.createElement('textarea');
                    textArea.value = inviteKey;
                    document.body.appendChild(textArea);
                    textArea.select();
                    document.execCommand('copy');
                    document.body.removeChild(textArea);
                    
                    const button = document.getElementById('copy-feedback');
                    if (button) {
                      button.textContent = 'Copied!';
                      button.classList.add('text-green-600');
                      setTimeout(() => {
                        button.textContent = 'Click anywhere on the code area to copy';
                        button.classList.remove('text-green-600');
                      }, 2000);
                    }
                  }
                }}
                title="Click to copy to clipboard"
              >
                <div className="flex items-center justify-center gap-3">
                  <p className="text-2xl font-bold text-gray-900 font-mono tracking-wider select-all">
                    {inviteKey}
                  </p>
                  <div className="p-1 text-[#0050c6] group-hover:text-[#003d9c] transition-colors duration-200">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                    </svg>
                  </div>
                </div>
              </div>
              <p id="copy-feedback" className="text-xs text-gray-600 mt-2 transition-colors duration-200">Click anywhere on the code area to copy</p>
            </div>
          </div>
        ) : (
          <div className="mb-8">
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-center">
              <p className="text-red-600">No invite code found in the URL</p>
            </div>
          </div>
        )}

        <div className="space-y-6">
          <h2 className="text-xl font-semibold text-gray-900 text-center mb-6">Get Started in 3 Easy Steps</h2>
          
          {/* Step 1 */}
          <div className="flex items-start gap-4 p-4 bg-gray-50 rounded-lg">
            <div className="flex-shrink-0 w-8 h-8 bg-[#0050c6] text-white rounded-full flex items-center justify-center font-bold text-sm">
              1
            </div>
            <div>
              <h3 className="font-semibold text-gray-900 mb-2">Download TimeKap</h3>
              <p className="text-sm text-gray-600 mb-3">Get the app from your device's app store</p>
              <div className="space-y-2">
                <button
                  onClick={() => handleDownload('ios')}
                  className="w-full bg-black text-white py-2 px-4 rounded-lg text-sm font-medium hover:bg-gray-800 transition-colors duration-200 flex items-center justify-center gap-2"
                >
                  <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
                  </svg>
                  Download for iOS
                </button>
                <button
                  onClick={() => handleDownload('android')}
                  className="w-full bg-green-600 text-white py-2 px-4 rounded-lg text-sm font-medium hover:bg-green-700 transition-colors duration-200 flex items-center justify-center gap-2"
                >
                  <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M3,20.5V3.5C3,2.91 3.34,2.39 3.84,2.15L13.69,12L3.84,21.85C3.34,21.6 3,21.09 3,20.5M16.81,15.12L6.05,21.34L14.54,12.85L16.81,15.12M20.16,10.81C20.5,11.08 20.75,11.5 20.75,12C20.75,12.5 20.53,12.9 20.18,13.18L17.89,14.5L15.39,12L17.89,9.5L20.16,10.81M6.05,2.66L16.81,8.88L14.54,11.15L6.05,2.66Z"/>
                  </svg>
                  Download for Android
                </button>
              </div>
            </div>
          </div>

          {/* Step 2 */}
          <div className="flex items-start gap-4 p-4 bg-gray-50 rounded-lg">
            <div className="flex-shrink-0 w-8 h-8 bg-[#f8482e] text-white rounded-full flex items-center justify-center font-bold text-sm">
              2
            </div>
            <div>
              <h3 className="font-semibold text-gray-900 mb-2">Copy Your Invite Code</h3>
              <p className="text-sm text-gray-600">
                Open the app and copy your invite code:
                <span className="font-mono font-bold text-[#0050c6] ml-1">
                  {inviteKey || '[Your Code]'}
                </span>
              </p>
            </div>
          </div>

          {/* Step 3 */}
          <div className="flex items-start gap-4 p-4 bg-gray-50 rounded-lg">
            <div className="flex-shrink-0 w-8 h-8 bg-[#ffce3e] text-black rounded-full flex items-center justify-center font-bold text-sm">
              3
            </div>
            <div>
              <h3 className="font-semibold text-gray-900 mb-2">Join Your TimeKap</h3>
              <p className="text-sm text-gray-600">
                You'll automatically be added to the TimeKap that invited you. Start sharing memories, sending surprise notes, and creating moments together!
              </p>
            </div>
          </div>
        </div>

        <div className="mt-8 pt-6 border-t border-gray-200 text-center">
          <p className="text-xs text-gray-500">
            TimeKap helps you stay close to loved ones through shared memory capsules, surprise notes, and fun challenges.
          </p>
        </div>
      </div>
    </div>
  );
}

function PrivacyPolicy() {
  const navigate = useNavigate();
  
  useEffect(() => {
    document.title = 'Privacy Policy - TimeKap';
  }, []);


  return (
    <div className="bg-[#0050c6] text-white min-h-screen">
      <div className="max-w-4xl mx-auto px-4 py-8">
        <button 
          onClick={() => navigate('/')}
          type="button"
          className="mb-6 bg-white text-[#0050c6] hover:bg-gray-100 px-4 py-2 rounded-lg font-medium transition-colors duration-200 flex items-center gap-2"
        >
          ← Back to Home
        </button>
        <h1 className="text-3xl font-bold mb-8">Privacy Policy</h1>
        <div className="prose prose-invert max-w-none">
          <div className="space-y-6">
            <p className="text-sm text-gray-300">Last updated: August 19, 2025</p>
            
            <p>This Privacy Policy describes Our policies and procedures on the collection, use and disclosure of Your information when You use the Service and tells You about Your privacy rights and how the law protects You.</p>
            
            <p>We use Your Personal data to provide and improve the Service. By using the Service, You agree to the collection and use of information in accordance with this Privacy Policy. This Privacy Policy has been created with the help of the Privacy Policy Generator.</p>
            
            <h2 className="text-2xl font-bold mt-8 mb-4">Interpretation and Definitions</h2>
            
            <h3 className="text-xl font-semibold mt-6 mb-3">Interpretation</h3>
            <p>The words of which the initial letter is capitalized have meanings defined under the following conditions. The following definitions shall have the same meaning regardless of whether they appear in singular or in plural.</p>
            
            <h3 className="text-xl font-semibold mt-6 mb-3">Definitions</h3>
            <p>For the purposes of this Privacy Policy:</p>
            
            <ul className="list-disc pl-6 space-y-2">
              <li><strong>Account</strong> means a unique account created for You to access our Service or parts of our Service.</li>
              <li><strong>Affiliate</strong> means an entity that controls, is controlled by or is under common control with a party, where "control" means ownership of 50% or more of the shares, equity interest or other securities entitled to vote for election of directors or other managing authority.</li>
              <li><strong>Application</strong> refers to TimeKap, the software program provided by the Company.</li>
              <li><strong>Company</strong> (referred to as either "the Company", "We", "Us" or "Our" in this Agreement) refers to TimeKap, Inc., 131 Continental Dr, Suite 305, Newark, DE 19713 US.</li>
              <li><strong>Cookies</strong> are small files that are placed on Your computer, mobile device or any other device by a website, containing the details of Your browsing history on that website among its many uses.</li>
              <li><strong>Country</strong> refers to: Delaware, United States</li>
              <li><strong>Device</strong> means any device that can access the Service such as a computer, a cellphone or a digital tablet.</li>
              <li><strong>Personal Data</strong> is any information that relates to an identified or identifiable individual.</li>
              <li><strong>Service</strong> refers to the Application or the Website or both.</li>
              <li><strong>Service Provider</strong> means any natural or legal person who processes the data on behalf of the Company. It refers to third-party companies or individuals employed by the Company to facilitate the Service, to provide the Service on behalf of the Company, to perform services related to the Service or to assist the Company in analyzing how the Service is used.</li>
              <li><strong>Third-party Social Media Service</strong> refers to any website or any social network website through which a User can log in or create an account to use the Service.</li>
              <li><strong>Usage Data</strong> refers to data collected automatically, either generated by the use of the Service or from the Service infrastructure itself (for example, the duration of a page visit).</li>
              <li><strong>Website</strong> refers to TimeKap, accessible from www.timekap.app</li>
              <li><strong>You</strong> means the individual accessing or using the Service, or the company, or other legal entity on behalf of which such individual is accessing or using the Service, as applicable.</li>
            </ul>
            
            <h2 className="text-2xl font-bold mt-8 mb-4">Collecting and Using Your Personal Data</h2>
            
            <h3 className="text-xl font-semibold mt-6 mb-3">Types of Data Collected</h3>
            
            <h4 className="text-lg font-medium mt-4 mb-2">Personal Data</h4>
            <p>While using Our Service, We may ask You to provide Us with certain personally identifiable information that can be used to contact or identify You. Personally identifiable information may include, but is not limited to:</p>
            
            <ul className="list-disc pl-6 space-y-1">
              <li>Email address</li>
              <li>First name and last name</li>
              <li>Phone number</li>
              <li>Address, State, Province, ZIP/Postal code, City</li>
              <li>Usage Data</li>
            </ul>
            
            <h4 className="text-lg font-medium mt-4 mb-2">Usage Data</h4>
            <p>Usage Data is collected automatically when using the Service.</p>
            
            <p>Usage Data may include information such as Your Device's Internet Protocol address (e.g. IP address), browser type, browser version, the pages of our Service that You visit, the time and date of Your visit, the time spent on those pages, unique device identifiers and other diagnostic data.</p>
            
            <p>When You access the Service by or through a mobile device, We may collect certain information automatically, including, but not limited to, the type of mobile device You use, Your mobile device unique ID, the IP address of Your mobile device, Your mobile operating system, the type of mobile Internet browser You use, unique device identifiers and other diagnostic data.</p>
            
            <p>We may also collect information that Your browser sends whenever You visit our Service or when You access the Service by or through a mobile device.</p>
            
            <h4 className="text-lg font-medium mt-4 mb-2">Information from Third-Party Social Media Services</h4>
            <p>The Company allows You to create an account and log in to use the Service through the following Third-party Social Media Services:</p>
            
            <ul className="list-disc pl-6 space-y-1">
              <li>Google</li>
              <li>Facebook</li>
              <li>Instagram</li>
              <li>Twitter</li>
              <li>LinkedIn</li>
            </ul>
            
            <p>If You decide to register through or otherwise grant us access to a Third-Party Social Media Service, We may collect Personal data that is already associated with Your Third-Party Social Media Service's account, such as Your name, Your email address, Your activities or Your contact list associated with that account.</p>
            
            <p>You may also have the option of sharing additional information with the Company through Your Third-Party Social Media Service's account. If You choose to provide such information and Personal Data, during registration or otherwise, You are giving the Company permission to use, share, and store it in a manner consistent with this Privacy Policy.</p>
            
            <h4 className="text-lg font-medium mt-4 mb-2">Information Collected while Using the Application</h4>
            <p>While using Our Application, in order to provide features of Our Application, We may collect, with Your prior permission:</p>
            
            <ul className="list-disc pl-6 space-y-1">
              <li>Information regarding your location</li>
              <li>Information from your Device's phone book (contacts list)</li>
              <li>Pictures and other information from your Device's camera and photo library</li>
            </ul>
            
            <p>We use this information to provide features of Our Service, to improve and customize Our Service. The information may be uploaded to the Company's servers and/or a Service Provider's server or it may be simply stored on Your device.</p>
            
            <p>You can enable or disable access to this information at any time, through Your Device settings.</p>
            
            <h3 className="text-xl font-semibold mt-6 mb-3">Tracking Technologies and Cookies</h3>
            <p>We use Cookies and similar tracking technologies to track the activity on Our Service and store certain information. Tracking technologies used are beacons, tags, and scripts to collect and track information and to improve and analyze Our Service. The technologies We use may include:</p>
            
            <ul className="list-disc pl-6 space-y-3">
              <li><strong>Cookies or Browser Cookies.</strong> A cookie is a small file placed on Your Device. You can instruct Your browser to refuse all Cookies or to indicate when a Cookie is being sent. However, if You do not accept Cookies, You may not be able to use some parts of our Service. Unless you have adjusted Your browser setting so that it will refuse Cookies, our Service may use Cookies.</li>
              <li><strong>Web Beacons.</strong> Certain sections of our Service and our emails may contain small electronic files known as web beacons (also referred to as clear gifs, pixel tags, and single-pixel gifs) that permit the Company, for example, to count users who have visited those pages or opened an email and for other related website statistics (for example, recording the popularity of a certain section and verifying system and server integrity).</li>
            </ul>
            
            <p>Cookies can be "Persistent" or "Session" Cookies. Persistent Cookies remain on Your personal computer or mobile device when You go offline, while Session Cookies are deleted as soon as You close Your web browser. You can learn more about cookies on TermsFeed website article.</p>
            
            <p>We use both Session and Persistent Cookies for the purposes set out below:</p>
            
            <div className="space-y-4">
              <div>
                <h4 className="text-lg font-medium">Necessary / Essential Cookies</h4>
                <p><strong>Type:</strong> Session Cookies</p>
                <p><strong>Administered by:</strong> Us</p>
                <p><strong>Purpose:</strong> These Cookies are essential to provide You with services available through the Website and to enable You to use some of its features. They help to authenticate users and prevent fraudulent use of user accounts. Without these Cookies, the services that You have asked for cannot be provided, and We only use these Cookies to provide You with those services.</p>
              </div>
              
              <div>
                <h4 className="text-lg font-medium">Cookies Policy / Notice Acceptance Cookies</h4>
                <p><strong>Type:</strong> Persistent Cookies</p>
                <p><strong>Administered by:</strong> Us</p>
                <p><strong>Purpose:</strong> These Cookies identify if users have accepted the use of cookies on the Website.</p>
              </div>
              
              <div>
                <h4 className="text-lg font-medium">Functionality Cookies</h4>
                <p><strong>Type:</strong> Persistent Cookies</p>
                <p><strong>Administered by:</strong> Us</p>
                <p><strong>Purpose:</strong> These Cookies allow us to remember choices You make when You use the Website, such as remembering your login details or language preference. The purpose of these Cookies is to provide You with a more personal experience and to avoid You having to re-enter your preferences every time You use the Website.</p>
              </div>
            </div>
            
            <p>For more information about the cookies we use and your choices regarding cookies, please visit our Cookies Policy or the Cookies section of our Privacy Policy.</p>
            
            <h2 className="text-2xl font-bold mt-8 mb-4">Use of Your Personal Data</h2>
            <p>The Company may use Personal Data for the following purposes:</p>
            
            <ul className="list-disc pl-6 space-y-2">
              <li><strong>To provide and maintain our Service,</strong> including to monitor the usage of our Service.</li>
              <li><strong>To manage Your Account:</strong> to manage Your registration as a user of the Service. The Personal Data You provide can give You access to different functionalities of the Service that are available to You as a registered user.</li>
              <li><strong>For the performance of a contract:</strong> the development, compliance and undertaking of the purchase contract for the products, items or services You have purchased or of any other contract with Us through the Service.</li>
              <li><strong>To contact You:</strong> To contact You by email, telephone calls, SMS, or other equivalent forms of electronic communication, such as a mobile application's push notifications regarding updates or informative communications related to the functionalities, products or contracted services, including the security updates, when necessary or reasonable for their implementation.</li>
              <li><strong>To provide You</strong> with news, special offers and general information about other goods, services and events which we offer that are similar to those that you have already purchased or enquired about unless You have opted not to receive such information.</li>
              <li><strong>To manage Your requests:</strong> To attend and manage Your requests to Us.</li>
              <li><strong>For business transfers:</strong> We may use Your information to evaluate or conduct a merger, divestiture, restructuring, reorganization, dissolution, or other sale or transfer of some or all of Our assets, whether as a going concern or as part of bankruptcy, liquidation, or similar proceeding, in which Personal Data held by Us about our Service users is among the assets transferred.</li>
              <li><strong>For other purposes:</strong> We may use Your information for other purposes, such as data analysis, identifying usage trends, determining the effectiveness of our promotional campaigns and to evaluate and improve our Service, products, services, marketing and your experience.</li>
            </ul>
            
            <p>We may share Your personal information in the following situations:</p>
            
            <ul className="list-disc pl-6 space-y-2">
              <li><strong>With Service Providers:</strong> We may share Your personal information with Service Providers to monitor and analyze the use of our Service, to contact You.</li>
              <li><strong>For business transfers:</strong> We may share or transfer Your personal information in connection with, or during negotiations of, any merger, sale of Company assets, financing, or acquisition of all or a portion of Our business to another company.</li>
              <li><strong>With Affiliates:</strong> We may share Your information with Our affiliates, in which case we will require those affiliates to honor this Privacy Policy. Affiliates include Our parent company and any other subsidiaries, joint venture partners or other companies that We control or that are under common control with Us.</li>
              <li><strong>With business partners:</strong> We may share Your information with Our business partners to offer You certain products, services or promotions.</li>
              <li><strong>With other users:</strong> when You share personal information or otherwise interact in the public areas with other users, such information may be viewed by all users and may be publicly distributed outside. If You interact with other users or register through a Third-Party Social Media Service, Your contacts on the Third-Party Social Media Service may see Your name, profile, pictures and description of Your activity. Similarly, other users will be able to view descriptions of Your activity, communicate with You and view Your profile.</li>
              <li><strong>With Your consent:</strong> We may disclose Your personal information for any other purpose with Your consent.</li>
            </ul>
            
            <h2 className="text-2xl font-bold mt-8 mb-4">Retention of Your Personal Data</h2>
            <p>The Company will retain Your Personal Data only for as long as is necessary for the purposes set out in this Privacy Policy. We will retain and use Your Personal Data to the extent necessary to comply with our legal obligations (for example, if we are required to retain your data to comply with applicable laws), resolve disputes, and enforce our legal agreements and policies.</p>
            
            <p>The Company will also retain Usage Data for internal analysis purposes. Usage Data is generally retained for a shorter period of time, except when this data is used to strengthen the security or to improve the functionality of Our Service, or We are legally obligated to retain this data for longer time periods.</p>
            
            <h2 className="text-2xl font-bold mt-8 mb-4">Transfer of Your Personal Data</h2>
            <p>Your information, including Personal Data, is processed at the Company's operating offices and in any other places where the parties involved in the processing are located. It means that this information may be transferred to — and maintained on — computers located outside of Your state, province, country or other governmental jurisdiction where the data protection laws may differ than those from Your jurisdiction.</p>
            
            <p>Your consent to this Privacy Policy followed by Your submission of such information represents Your agreement to that transfer.</p>
            
            <p>The Company will take all steps reasonably necessary to ensure that Your data is treated securely and in accordance with this Privacy Policy and no transfer of Your Personal Data will take place to an organization or a country unless there are adequate controls in place including the security of Your data and other personal information.</p>
            
            <h2 className="text-2xl font-bold mt-8 mb-4">Delete Your Personal Data</h2>
            <p>You have the right to delete or request that We assist in deleting the Personal Data that We have collected about You.</p>
            
            <p>Our Service may give You the ability to delete certain information about You from within the Service.</p>
            
            <p>You may update, amend, or delete Your information at any time by signing in to Your Account, if you have one, and visiting the account settings section that allows you to manage Your personal information. You may also contact Us to request access to, correct, or delete any personal information that You have provided to Us.</p>
            
            <p>Please note, however, that We may need to retain certain information when we have a legal obligation or lawful basis to do so.</p>
            
            <h2 className="text-2xl font-bold mt-8 mb-4">Disclosure of Your Personal Data</h2>
            
            <h3 className="text-xl font-semibold mt-6 mb-3">Business Transactions</h3>
            <p>If the Company is involved in a merger, acquisition or asset sale, Your Personal Data may be transferred. We will provide notice before Your Personal Data is transferred and becomes subject to a different Privacy Policy.</p>
            
            <h3 className="text-xl font-semibold mt-6 mb-3">Law enforcement</h3>
            <p>Under certain circumstances, the Company may be required to disclose Your Personal Data if required to do so by law or in response to valid requests by public authorities (e.g. a court or a government agency).</p>
            
            <h3 className="text-xl font-semibold mt-6 mb-3">Other legal requirements</h3>
            <p>The Company may disclose Your Personal Data in the good faith belief that such action is necessary to:</p>
            
            <ul className="list-disc pl-6 space-y-1">
              <li>Comply with a legal obligation</li>
              <li>Protect and defend the rights or property of the Company</li>
              <li>Prevent or investigate possible wrongdoing in connection with the Service</li>
              <li>Protect the personal safety of Users of the Service or the public</li>
              <li>Protect against legal liability</li>
            </ul>
            
            <h2 className="text-2xl font-bold mt-8 mb-4">Security of Your Personal Data</h2>
            <p>The security of Your Personal Data is important to Us, but remember that no method of transmission over the Internet, or method of electronic storage is 100% secure. While We strive to use commercially acceptable means to protect Your Personal Data, We cannot guarantee its absolute security.</p>
            
            <h2 className="text-2xl font-bold mt-8 mb-4">Children's Privacy</h2>
            <p>Our Service does not address anyone under the age of 13. We do not knowingly collect personally identifiable information from anyone under the age of 13. If You are a parent or guardian and You are aware that Your child has provided Us with Personal Data, please contact Us. If We become aware that We have collected Personal Data from anyone under the age of 13 without verification of parental consent, We take steps to remove that information from Our servers.</p>
            
            <p>If We need to rely on consent as a legal basis for processing Your information and Your country requires consent from a parent, We may require Your parent's consent before We collect and use that information.</p>
            
            <h2 className="text-2xl font-bold mt-8 mb-4">Links to Other Websites</h2>
            <p>Our Service may contain links to other websites that are not operated by Us. If You click on a third party link, You will be directed to that third party's site. We strongly advise You to review the Privacy Policy of every site You visit.</p>
            
            <p>We have no control over and assume no responsibility for the content, privacy policies or practices of any third party sites or services.</p>
            
            <h2 className="text-2xl font-bold mt-8 mb-4">Changes to this Privacy Policy</h2>
            <p>We may update Our Privacy Policy from time to time. We will notify You of any changes by posting the new Privacy Policy on this page.</p>
            
            <p>We will let You know via email and/or a prominent notice on Our Service, prior to the change becoming effective and update the "Last updated" date at the top of this Privacy Policy.</p>
            
            <p>You are advised to review this Privacy Policy periodically for any changes. Changes to this Privacy Policy are effective when they are posted on this page.</p>
            
            <h2 className="text-2xl font-bold mt-8 mb-4">Contact Us</h2>
            <p>If you have any questions about this Privacy Policy, You can contact us:</p>
            
            <p>By email: <a href="mailto:connect@timekap.app" className="text-blue-300 hover:text-blue-200 underline">connect@timekap.app</a></p>
          </div>
        </div>
      </div>
    </div>
  );
}

function TermsAndConditions() {
  const navigate = useNavigate();
  
  useEffect(() => {
    document.title = 'Terms and Conditions - TimeKap';
  }, []);


  return (
    <div className="bg-[#0050c6] text-white min-h-screen">
      <div className="max-w-4xl mx-auto px-4 py-8">
        <button 
          onClick={() => navigate('/')}
          type="button"
          className="mb-6 bg-white text-[#0050c6] hover:bg-gray-100 px-4 py-2 rounded-lg font-medium transition-colors duration-200 flex items-center gap-2"
        >
          ← Back to Home
        </button>
        <h1 className="text-3xl font-bold mb-8">Terms and Conditions</h1>
        <div> Coming Soon </div>
      </div>
    </div>
  );
}

// Home Page Component
function HomePage() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [animatedFeatures, setAnimatedFeatures] = useState(new Set());
  const [heroImageLoaded, setHeroImageLoaded] = useState(false);
  const featureRefs = useRef([]);
  const discordLink = 'https://discord.gg/3CgayCbu';
  useEffect(() => {
    document.title = 'TimeKap - Kapture Life\'s Best Moments';
  }, []);

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };


  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const index = parseInt(entry.target.dataset.index);
            setAnimatedFeatures(prev => new Set([...prev, index]));
          }
        });
      },
      {
        threshold: 0.2, // Trigger when 20% of the element is visible
        rootMargin: '0px 0px -50px 0px' // Start animation slightly before element enters viewport
      }
    );

    const currentRefs = featureRefs.current;
    currentRefs.forEach((ref) => {
      if (ref) observer.observe(ref);
    });

    return () => {
      currentRefs.forEach((ref) => {
        if (ref) observer.unobserve(ref);
      });
    };
  }, []);

  return (
    <div className="App">
      {/* Full Page Loading Spinner */}
      {!heroImageLoaded && (
        <div className="loading-overlay">
          <div className="loading-spinner">
            <img 
              src="/logo-long.png" 
              alt="TimeKap Logo" 
              className="loading-logo"
            />
          </div>
        </div>
      )}
      {/* Responsive Navbar */}
      <header className="bg-[#ffce3e] w-full top-0 z-50" role="banner">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 bg-[#ffce3e]">
          <div className="flex justify-between items-center h-32">
            {/* Logo */}
            <div className="flex-shrink-0">
              <a href="/" className="flex items-center">
                <img 
                  alt="TimeKap logo" 
                  src="/logo-long.png" 
                  className="h-16 w-auto sm:h-14" // Increased from h-8/h-10 to h-12/h-14
                />
              </a>
            </div>
            {/* Desktop Menu */}
            <nav className="hidden md:flex space-x-12 items-center" role="navigation" aria-label="Main navigation">
              <a href="#home" className="text-[#3a3a3a] hover:text-[#0050c6] px-5 py-4 text-lg font-medium transition-colors duration-200" aria-label="Go to home section">
                Home
              </a>
              <a href="#features" className="text-[#3a3a3a] hover:text-[#0050c6] px-5 py-4 text-lg font-medium transition-colors duration-200" aria-label="View app features">
                Features
              </a>
              <a href={earlyAccessFormLink} className="bg-[#f8482e] text-white hover:bg-[#0050c6] px-5 py-4 rounded-full text-lg font-medium transition-colors duration-200 flex items-center gap-2" target="_blank" rel="noopener noreferrer" aria-label="Get Early Access">
                {/* <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515a.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0a12.64 12.64 0 0 0-.617-1.25a.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057a19.9 19.9 0 0 0 5.993 3.03a.078.078 0 0 0 .084-.028a14.09 14.09 0 0 0 1.226-1.994a.076.076 0 0 0-.041-.106a13.107 13.107 0 0 1-1.872-.892a.077.077 0 0 1-.008-.128a10.2 10.2 0 0 0 .372-.292a.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127a12.299 12.299 0 0 1-1.873.892a.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028a19.839 19.839 0 0 0 6.002-3.03a.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419c0-1.333.956-2.419 2.157-2.419c1.21 0 2.176 1.096 2.157 2.42c0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419c0-1.333.955-2.419 2.157-2.419c1.21 0 2.176 1.096 2.157 2.42c0 1.333-.946 2.418-2.157 2.418z"/>
                </svg> */}
                <div>🎉</div>
                Join Waitlist
              </a>
            </nav>

            {/* Mobile menu button */}
            <div className="md:hidden">
              <button
                onClick={toggleMenu}
                className="text-[#3a3a3a] hover:text-[#0050c6] focus:outline-none p-4"
                aria-label="Toggle menu"
              >
                <svg 
                  className="h-8 w-8" 
                  fill="none" 
                  viewBox="0 0 24 24" 
                  stroke="currentColor"
                >
                  {isMenuOpen ? (
                    <path 
                      strokeLinecap="round" 
                      strokeLinejoin="round" 
                      strokeWidth={2} 
                      d="M6 18L18 6M6 6l12 12" 
                    />
                  ) : (
                    <path 
                      strokeLinecap="round" 
                      strokeLinejoin="round" 
                      strokeWidth={2} 
                      d="M4 6h16M4 12h16M4 18h16" 
                    />
                  )}
                </svg>
              </button>
            </div>
          </div>

          {/* Mobile Menu */}
          <div className={`text-center pb-4 rounded-3xl md:hidden transition-all duration-300 ease-in-out ${isMenuOpen ? 'max-h-64 opacity-100' : 'max-h-0 opacity-0'} overflow-hidden`}>
            <nav className="px-2 py-2 pb-4 space-y-1 bg-white border-t border-gray-200 rounded-3xl" role="navigation" aria-label="Mobile navigation">
              <a 
                href="#home" 
                className="block text-[#3a3a3a] hover:text-[#0050c6] hover:bg-gray-50 px-3 py-2 text-base font-medium rounded-2xl transition-colors duration-200"
                onClick={() => setIsMenuOpen(false)}
              >
                Home
              </a>
              <a 
                href="#features" 
                className="block text-center text-[#3a3a3a] hover:text-[#0050c6] hover:bg-gray-50 px-3 py-2 text-base font-medium rounded-2xl transition-colors duration-200"
                onClick={() => setIsMenuOpen(false)}
              >
                Features
              </a>
              <a 
                href={discordLink} 
                className="flex items-center justify-center gap-2 bg-[#f8482e] text-white hover:bg-[#0050c6] px-3 py-2 text-base font-medium rounded-2xl transition-colors duration-200"
                onClick={() => setIsMenuOpen(false)}
              >
                <div>🎉</div>
                Join Waitlist
              </a>
            </nav>
          </div>
        </div>
      </header>

      {/* Main content with top padding to account for fixed navbar */}
      <main className="bg-[#fdf8f3]" id="home">
        <section className="relative hero-section min-h-[96vh] sm:min-h-[80vh] pt-4" aria-labelledby="hero-heading">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-16">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
              <div className="text-center lg:text-left lg:pb-16">
                <h1 id="hero-heading" className="text-4xl sm:text-5xl lg:text-6xl font-bold leading-tight text-[#f8482e] pb-4 lg:pb-8">
                  Kapture Life&apos;s Best Moments.
                </h1>
                <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold leading-tight text-[#0050c6] pb-4">
                  and keep them forever.
                </h2>
                <p className="text-lg text-gray-600 mt-4 leading-relaxed">
                  TimeKap helps you stay close to the people you love, even when
                  life gets busy.
                </p>
                <p className="text-lg text-gray-600 mt-4 leading-relaxed">
                  Build your own memory kapsule together: share random moments,
                  send surprise notes, do fun challenges, and keep the things that
                  matter through quick interactions throughout the week.
                </p>
              </div>
              <div className="flex justify-center lg:justify-end">
                <img
                  alt="TimeKap app interface showing family sharing memories in a beautiful digital memory capsule with colorful moments and photos"
                  src="/hero-image.png"
                  className="max-w-full h-auto rounded-lg pt-4 hero-image-continuous-float"
                  onLoad={() => setHeroImageLoaded(true)}
                  loading="eager"
                  width="600"
                  height="400"
                />
              </div>
            </div>
          </div>
          {/* <div className="text-center pt-12 sm:pt-8 md:pt-8 py-8 sm:py-0 md:my-12"> */}
          <div className="text-center pt-12 pb-6 sm:pt-14 md:pt-16 lg:pt-16">
            <div className="flex flex-col wmdsm:flex-row gap-4 md:gap-6 justify-center items-center">
                                <button 
                    type="button" 
                    className="bg-[#f8482e] text-[#fdf8f3] hover:bg-[#0050c6] px-8 sm:px-12 md:px-10 lg:px-12 py-4 sm:py-5 md:py-6 lg:py-5 rounded-full text-2xl sm:text-3xl md:text-3xl lg:text-4xl font-semibold transition-colors duration-200 shadow-lg hover:shadow-xl transform hover:-translate-y-1"
                    onClick={() => window.location.href = earlyAccessFormLink}
                  >
                    <span>🎉 </span>
                    Get Early Access
                  </button>
            </div>
          </div>
        </section>

        {/* Features section */}
        <section className="bg-[#fdf8f3] pb-4 pt-24 md:pt-40 lg:pt-30" id="features" aria-labelledby="features-heading">
          <div className="max-w-[90rem] mx-auto px-4 sm:px-6 lg:px-8">
            <h2 id="features-heading" className="sr-only">TimeKap Features</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-2 xl:grid-cols-4">
              {[
                { src: "/features/1.png", alt: "Stay Close" },
                { src: "/features/2.png", alt: "Kapture the Moments" },
                { src: "/features/3.png", alt: "Shape Your Story" },
                { src: "/features/4.png", alt: "Make a Memory" }
              ].map((feature, index) => (
                <div key={index} className="text-center">
                  <img
                    ref={el => featureRefs.current[index] = el}
                    src={feature.src}
                    alt={`${feature.alt} - ${index === 0 ? 'Connect with family and friends no matter the distance' : index === 1 ? 'Capture and share spontaneous life moments easily' : index === 2 ? 'Create personalized memory stories together' : 'Build lasting digital memory capsules with loved ones'}`}
                    data-index={index}
                    data-delay={index}
                    className={`mx-auto h-100 xl:h-92 w-auto mb-6 p-4 feature-image ${
                      animatedFeatures.has(index) ? 'animate' : ''
                    }`}
                    loading="lazy"
                    width="300"
                    height="300"
                  />
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Bottom section */}
        <section className="relative bg-[#0050c6]" aria-labelledby="cta-heading">
          <div className="relative">
            <img 
              src="/squiggle-300h.png"
              alt="Decorative wave design element transitioning from cream to blue background"
              className="relative z-20 w-full h-auto md:w-full"
              loading="lazy"
              width="1200"
              height="300"
            />
            
            {/* Content section with blue background */}
            <div className="bg-[#0050c6] relative pb-16 w-full">
              <div className="relative text-center pt-16">
                <h2 id="cta-heading" className="text-4xl font-bold text-white mb-20">Be Part of TimeKap.</h2>
                <div className="flex flex-col md:flex-row gap-4 justify-center items-center">
                  <button 
                    type="button" 
                    className="flex items-center gap-3 bg-[#f8482e] text-[#fdf8f3] hover:bg-[#ffce3e] hover:text-[#3a3a3a] px-12 py-5 mb-4 rounded-full text-4xl font-semibold transition-colors duration-200 shadow-lg hover:shadow-xl transform hover:-translate-y-1"
                    onClick={() => window.location.href = earlyAccessFormLink}
                  >
                    <div>
                    🎉
                    </div>
                    {/* <svg className="h-8 w-8" fill="currentColor" viewBox="0 0 64 64">

<path fill="#f7b600" d="M2 61l8.6-3l-6.5-3z">

</path>

<path fill="#ffdd7d" d="M26.9 36.4L14.8 24.2l-2 5.6z">

</path>

<path fill="#f7b600" d="M12.8 29.8l-2.2 6.3l26.8 12.5l1.3-.4l-11.8-11.8z">

</path>

<path fill="#ffdd7d" d="M8.5 42.4l20 9.3l8.9-3.1l-26.8-12.5z">

</path>

<path fill="#f7b600" d="M6.3 48.7l13.2 6.2l9-3.2l-20-9.3z">

</path>

<path fill="#ffdd7d" d="M6.3 48.7L4.1 55l6.5 3l8.9-3.1z">

</path>

<path d="M31.9 31.2c6.7 6.6 10.2 14 7.8 16.4c-2.5 2.4-9.9-1-16.7-7.7c-6.7-6.6-10.2-14-7.8-16.4c2.5-2.4 9.9 1.1 16.7 7.7" fill="#493816">

</path>

<path d="M23.5 14.5c-1.6-2.3.1-3.3 2.3-2.9c-2.1-2.5-.8-4.3 2.5-3.6c1 .2-.4 1.9-1.3 1.9c2.7 2 1.2 4.2-1.7 3.7c2.6 3.5-1.8 2.6-3.8 2.8C21 19 24 22 23 22c-2.2 0-5.8-8.3.5-7.5" fill="#42ade2">

</path>

<path d="M44.5 19.3c-1.5.7-5.7-5.9-.5-6c-3-2.7-2.6-4 1.4-4.1c-4.6-4.6 2.7-6.2 3.4-3.8c.2.7-2.2-.6-3 .7c-.9 1.5 5.6 5.4-1.1 5.1c2.5 2.5 2.6 3.7-1.3 4.1c.5.8 2.1 3.6 1.1 4" fill="#ff8736">

</path>

<path d="M46.2 34.9l1.5-1.3s1.4 2.1 2.4 2.9c.8-3.6.6-5.7 4.7-3.3c-2.3-6.2 1.5-3.9 5.2-2.2c-.2-1.6 0-1.4 1.6-1.9c1.4 5.3-2.4 3.7-5.4 2c1.8 4.8-.1 4.5-3.9 2.9c-.1 2-.7 4.3-1.9 4.5c-1.4.4-4.2-3.6-4.2-3.6" fill="#ed4c5c">

</path>

<path d="M35 20.1c-1.8 2.4-4.7 3.7-6.8 5.8c-2.2 2.2-3.5 8.2-3.5 8.2s.8-6.3 2.9-8.7c1.9-2.2 4.7-3.8 6.2-6.3c2.6-4.6.2-10.6-3.2-14.1c.7-.6 1.7-1.4 2.2-2c3.3 4.1 6.1 12 2.2 17.1" fill="#c28fef">

</path>

<path d="M38.1 25.2c-2.6 1.9-4.5 4.7-6.3 7.3c-1.6 2.3-6.7 5.2-6.7 5.2s4.8-3.3 6.3-5.7c1.8-3 3.6-6.1 6.4-8.3c5.6-4.3 13.7-3.9 20-1.6c-.4.9-1.1 2.8-1.1 2.8s-13.3-3.6-18.6.3" fill="#ff8736">

</path>

<g fill="#42ade2">

<path d="M49.2 24.7c-1.7 2.2-2.5 4.9-3.8 7.4c-1.2 2.3-2.8 4.5-5.1 5.7c-2.6 1.3-8.3.9-8.3.9s5.7-.1 8.1-1.7c2.4-1.6 3.7-4.4 4.6-7c1.8-5 4-10.4 9.2-12.6c.3.9 1 2.8 1 2.8s-2.9.8-5.7 4.5">

</path>

<path d="M3.21 14.325l2.828-2.829l2.829 2.828l-2.828 2.83z">

</path>

</g>

<path fill="#ff8736" d="M7.173 23.197L10 20.369l2.828 2.828L10 26.025z">

</path>

<path fill="#ed4c5c" d="M14.358 9.822l2.828-2.828l2.828 2.828l-2.828 2.828z">

</path>

<path fill="#c28fef" d="M45.205 43.696l2.828-2.829l2.828 2.829l-2.828 2.828z">

</path>

<path fill="#ed4c5c" d="M38.903 53.39l2.828-2.828l2.829 2.829l-2.829 2.828z">

</path>

<path fill="#ff8736" d="M51.279 55.607l2.828-2.829l2.828 2.829l-2.828 2.828z">

</path>

<g fill="#42ade2">

<path d="M54.078 42.731l2.828-2.828l2.828 2.828l-2.828 2.829z">

</path>

<path d="M49.356 12.823l2.828-2.829l2.829 2.829l-2.828 2.828z">

</path>

</g>

<path fill="#ed4c5c" d="M19.044 29.792l2.829-2.828l2.828 2.828l-2.828 2.829z">

</path>

</svg> */}
                    Get Early Access
                  </button>
                  {/* <button 
                    type="button" 
                    className="bg-[#5865F2] text-[#fdf8f3] hover:bg-[#4752C4] px-20 py-5 mb-4 rounded-full text-4xl font-semibold transition-colors duration-200 shadow-lg hover:shadow-xl transform hover:-translate-y-1 flex items-center gap-3"
                    onClick={() => window.location.href = discordLink}
                  >
                    <svg className="h-8 w-8" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0 12.64 12.64 0 0 0-.617-1.25.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057 19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028 14.09 14.09 0 0 0 1.226-1.994.076.076 0 0 0-.041-.106 13.107 13.107 0 0 1-1.872-.892.077.077 0 0 1-.008-.128 10.2 10.2 0 0 0 .372-.292.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127 12.299 12.299 0 0 1-1.873.892.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028 19.839 19.839 0 0 0 6.002-3.03.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.956-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.955-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.946 2.418-2.157 2.418z"/>
                    </svg>
                    Join Beta
                  </button> */}
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>
                {/* Footer with Social Links */}
                <footer className="bg-[#0050c6] text-white pb-4" role="contentinfo">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex flex-col md:flex-row justify-between items-center">
              {/* Logo and company info */}
              <div className="flex items-center mb-6 lg:mb-0 hidden lg:flex">
                <img 
                  alt="TimeKap logo - circular icon with colorful time capsule design" 
                  src="favicon.svg" 
                  className="h-20 w-auto mr-4"
                  loading="lazy"
                  width="80"
                  height="80"
                />
                <div>
                  <p className="text-lg font-semibold">TimeKap</p>
                  <p className="text-sm text-gray-300">Kapture Life's Best Moments</p>
                </div>
              </div>
              
              {/* Social Media Links and Copyright */}
              <div className="flex flex-col md:flex-row items-center space-y-4 md:space-y-0 md:space-x-6">
                <div className="flex space-x-6 mx-10">
                  {/* X (formerly Twitter) */}
                  {/* <a 
                    href="https://x.com/timekapapp" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-gray-300 hover:text-[#ffce3e] transition-colors duration-200"
                    aria-label="X"
                  >
                    <svg className="h-6 w-6" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                    </svg>
                  </a> */}
                  {/* Instagram */}
                  {/* <a 
                    href="https://www.instagram.com/timekapapp" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-gray-300 hover:text-[#ffce3e] transition-colors duration-200"
                    aria-label="Instagram"
                  >
                    <svg className="h-6 w-6" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M12.017 0C5.396 0 .029 5.367.029 11.987c0 6.62 5.367 11.987 11.988 11.987 6.62 0 11.987-5.367 11.987-11.987C24.014 5.367 18.637.001 12.017.001zM8.449 16.988c-1.297 0-2.448-.49-3.323-1.297C4.198 14.895 3.708 13.744 3.708 12.447s.49-2.448 1.418-3.323c.875-.807 2.026-1.297 3.323-1.297s2.448.49 3.323 1.297c.928.875 1.418 2.026 1.418 3.323s-.49 2.448-1.418 3.244c-.875.807-2.026 1.297-3.323 1.297zm7.718-1.297c-.875.807-2.026 1.297-3.323 1.297s-2.448-.49-3.323-1.297c-.928-.875-1.418-2.026-1.418-3.323s.49-2.448 1.418-3.323c.875-.807 2.026-1.297 3.323-1.297s2.448.49 3.323 1.297c.928.875 1.418 2.026 1.418 3.323s-.49 2.448-1.418 3.244z"/>
                    </svg>
                  </a> */}
                  {/* Discord */}
                  <a 
                    href={discordLink}
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-gray-300 hover:text-[#ffce3e] transition-colors duration-200"
                    aria-label="Discord"
                  >
                    <svg className="h-6 w-6" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0 12.64 12.64 0 0 0-.617-1.25.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057 19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028 14.09 14.09 0 0 0 1.226-1.994.076.076 0 0 0-.041-.106 13.107 13.107 0 0 1-1.872-.892.077.077 0 0 1-.008-.128 10.2 10.2 0 0 0 .372-.292.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127 12.299 12.299 0 0 1-1.873.892.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028 19.839 19.839 0 0 0 6.002-3.03.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.956-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.955-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.946 2.418-2.157 2.418z"/>
                    </svg>
                  </a>
                  {/* TikTok */}
                  <a 
                    href="https://www.tiktok.com/@timekapapp" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-gray-300 hover:text-[#ffce3e] transition-colors duration-200"
                    aria-label="TikTok"
                  >
                    <svg className="h-6 w-6" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
                    </svg>
                  </a>
                  {/* Facebook */}
                  {/* <a 
                    href="https://www.facebook.com/profile.php?id=61578568301484" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-gray-300 hover:text-[#ffce3e] transition-colors duration-200"
                    aria-label="Facebook"
                  >
                    <svg className="h-6 w-6" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                    </svg>
                  </a> */}
                  {/* LinkedIn */}
                  <a 
                    href="https://www.linkedin.com/company/timekap" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-gray-300 hover:text-[#ffce3e] transition-colors duration-200"
                    aria-label="LinkedIn"
                  >
                    <svg className="h-6 w-6" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                    </svg>
                  </a>
                  {/* Email */}
                  <a 
                    href="mailto:emma.timekap@gmail.com" 
                    className="text-gray-300 hover:text-[#ffce3e] transition-colors duration-200"
                    aria-label="Email"
                  >
                    <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                    </svg>
                  </a>
                </div>
                <div className="flex flex-col sm:flex-row items-center space-y-2 sm:space-y-0 sm:space-x-4 text-sm text-gray-300">
                  <Link 
                    to="/privacy-policy"
                    className="hover:text-[#ffce3e] transition-colors duration-200 cursor-pointer"
                  >
                    Privacy Policy
                  </Link>
                  <span className="hidden sm:inline">•</span>
                  <Link 
                    to="/terms-and-conditions"
                    className="hover:text-[#ffce3e] transition-colors duration-200 cursor-pointer"
                  >
                    Terms & Conditions
                  </Link>
                  <span className="hidden sm:inline">•</span>
                  <p>© 2025 TimeKap™. All rights reserved.</p>
                </div>
              </div>
            </div>
          </div>
        </footer>
      </div>
  );
}

// 404 Not Found Component
function NotFound() {
  const navigate = useNavigate();
  
  useEffect(() => {
    document.title = 'Page Not Found - TimeKap';
  }, []);

  return (
    <div className="bg-[#0050c6] text-white min-h-screen flex items-center justify-center">
      <div className="text-center px-4">
        <h1 className="text-6xl font-bold mb-4">404</h1>
        <h2 className="text-2xl font-semibold mb-4">Page Not Found</h2>
        <p className="text-lg mb-8">The page you're looking for doesn't exist.</p>
        <button 
          onClick={() => navigate('/')}
          className="bg-[#f8482e] text-white hover:bg-[#ffce3e] hover:text-[#3a3a3a] px-8 py-3 rounded-full text-lg font-semibold transition-colors duration-200"
        >
          Go Home
        </button>
      </div>
    </div>
  );
}

// Main App Component with Router
function App() {
  return (
    <Router>
      <Routes>
        <Route path="/" element={<HomePage />} />
        <Route path="/privacy-policy" element={<PrivacyPolicy />} />
        <Route path="/terms-and-conditions" element={<TermsAndConditions />} />
        <Route path="/invite" element={<Invite />} />
        <Route path="*" element={<NotFound />} />
      </Routes>
    </Router>
  );
}

export default App;
